import { AxiosInstance } from 'axios';
import ResTable from 'models/api/table';
import { Key } from 'react';
import {
    AgreementProlongationAdd,
    BillRefundInfo,
    BillRollbackInfo,
    DocumentItem,
    LoanDetailInfo,
    Order,
    OrderAgreementProlongations,
    OrderChecksInfo,
    OrderComments,
    OrderDetailsGeneralInfo,
    OrderEditingInfo,
    OrderFilter,
    OrderInfo,
    OrderOperatorChange,
    OrderScoring,
    OrderScoringSpecialMark,
    OrderStatusChange,
    OrderStatusChangeShort,
    OrderTable,
    OrderUpdate,
} from '../models/api/order';

export const getOrders = async (axios: AxiosInstance, requestParams: OrderFilter) => {
    const params = { ...requestParams };
    const { data } = await axios.get<ResTable<OrderTable>>(`/order`, { params });
    return data;
};

export const getOrdersExcel = async (axios: AxiosInstance, params: OrderFilter) => {
    const { data } = await axios.get<Blob>('/order/excel', {
        responseType: 'arraybuffer',
        params: { ...params },
    });
    return data;
};

export const getOrderGeneralInfo = async (axios: AxiosInstance, id: Key) => {
    const { data } = await axios.get<OrderDetailsGeneralInfo>(`/order/detail/general/${id}`);
    return data;
};

export const getOrderChecksInfo = async (axios: AxiosInstance, id: number, clientId: number) => {
    const params = { clientId };
    const { data } = await axios.get<OrderChecksInfo>(`/order/detail/checks/${id}`, {
        params,
    });
    return data;
};

export const getOrderAgreementProlongations = async (axios: AxiosInstance, id: number) => {
    const { data } = await axios.get<OrderAgreementProlongations>(
        `/order/detail/agreementProlongations/${id}`
    );
    return data;
};

export const addOrderAgreementProlongation = async (
    axios: AxiosInstance,
    data: AgreementProlongationAdd
) => {
    await axios.post(`/order/detail/agreementProlongations`, data);
};

export const deleteOrderAgreementProlongation = async (
    axios: AxiosInstance,
    id: number,
    dsc: string
) => {
    await axios.delete(`/order/detail/agreementProlongations/${id}`, {
        params: { dsc },
    });
};

export const getOrderInfo = async (axios: AxiosInstance, id: Key) => {
    const { data } = await axios.get<OrderInfo>(`/order/detail/${id}`);
    return data;
};

export const getOrderCommentsInfo = async (axios: AxiosInstance, id: Key) => {
    const { data } = await axios.get<OrderComments[]>(`/order/detail/comments/${id}`);
    return data;
};

export const changeOrderSpecialMark = async (
    axios: AxiosInstance,
    info: OrderScoringSpecialMark
) => {
    const { data } = await axios.post(`/order/specialMark`, info);
    return data;
};

export const changeOrderItem = async (axios: AxiosInstance, item: OrderUpdate) => {
    const { data } = await axios.post<Order>(`/order/${item.id}`, item);
    return data;
};

export const changeOperator = async (axios: AxiosInstance, data: OrderOperatorChange) => {
    await axios.post<Order>(`/order/operatorchange`, data);
};

export const changeStatus = async (axios: AxiosInstance, data: OrderStatusChangeShort) => {
    await axios.post<Order>(`/order/statuschange`, data);
};

export const cancelOrder = async (axios: AxiosInstance, data: OrderStatusChange) => {
    await axios.post<Order>(`/order/statuschange/cancel`, data);
};

export const postponeOrder = async (axios: AxiosInstance, data: OrderStatusChangeShort) => {
    await axios.post<Order>(`/order/statuschange/postpone`, data);
};

export const orderDelete = async (axios: AxiosInstance, id: number) => {
    await axios.delete(`/order/${id}`);
};

export const addComment = async (axios: AxiosInstance, id: number, comment: string) => {
    const { data } = await axios.post<Order>(`/order/addComment/${id}`, comment);
    return data;
};

export const submitOrder = async (
    axios: AxiosInstance,
    id: number,
    submitType: string,
    scoringInfo: OrderScoring | null
) => {
    const { data } = await axios.post(`/order/submit/${id}`, scoringInfo, {
        params: { submitType },
    });
    return data;
};

export const getLoanDetailInfo = async (axios: AxiosInstance, filter: { loanId?: number }) => {
    const { data } = await axios.get<LoanDetailInfo>(`/order/detail/loanDetailInfo`, {
        params: { filter },
    });
    return data;
};

export const putLoanDetailInfo = async (axios: AxiosInstance, info: LoanDetailInfo) => {
    const { data } = await axios.put(`/order/detail/loanDetailInfo`, info);
    return data;
};

export const getRollbackInfoInfo = async (axios: AxiosInstance, billId: number) => {
    const { data } = await axios.get<BillRollbackInfo>(`/order/detail/billRollback/${billId}`);
    return data;
};

export const billRollback = async (axios: AxiosInstance, info: BillRollbackInfo) => {
    const { data } = await axios.post(`/order/detail/billRollback`, info);
    return data;
};

export const billRollup = async (axios: AxiosInstance, loanId: number) => {
    const { data } = await axios.post(`/order/detail/billRollup/${loanId}`);
    return data;
};

export const getBillRefundInfo = async (axios: AxiosInstance, loanId: number) => {
    const { data } = await axios.get<BillRefundInfo>(`/order/detail/billRefund/${loanId}`);
    return data;
};

export const billRefund = async (axios: AxiosInstance, info: BillRefundInfo) => {
    const { data } = await axios.post(`/order/detail/billRefund`, info);
    return data;
};

export const loanWriteOff = async (axios: AxiosInstance, loanId: string | number | undefined) => {
    const { data } = await axios.post(`/order/detail/loanWriteOff/${loanId}`);
    return data;
};

export const orderAssignToOperator = async (axios: AxiosInstance, id: number) => {
    await axios.post(`/order/assignToOperator/${id}`);
};

export const getDocuments = async (axios: AxiosInstance, id: number) => {
    const { data } = await axios.get<DocumentItem[]>(`/order/${id}/documents`);
    return data;
};

export const downloadDocument = async (axios: AxiosInstance, docId: number) => {
    const response = await axios.get<Blob>(`/order/documents/${docId}`, {
        responseType: 'arraybuffer',
    });
    return response.data;
};
export const getOverdueDaysCount = async (axios: AxiosInstance, id: number) => {
    const response = await axios.get<number>(`/order/documents/getOverdueDaysCount/${id}`);
    return response.data;
};

export const DocumentsGenerateTaskAdd = async (axios: AxiosInstance, id: number) => {
    await axios.post(`/order/documents/DocumentsGenerateTaskAdd/${id}`);
};

export const updateOrderAdditionalData = async (
    axios: AxiosInstance,
    id: number | undefined,
    info: OrderEditingInfo
) => {
    await axios.post('/order/updateAdditionalData', info, {
        params: { id: id },
    });
};

export const approveRepeatClient = async (axios: AxiosInstance, id: number) => {
    await axios.post(`/order/StatusChange/approval/${id}`);
};
